import { useEffect, useRef, useState } from 'react';

const MINIMUM_VISIBLE_MS = 150;
const MAXIMUM_VISIBLE_MS = 3000;
const FADE_OUT_MS = 200;
export interface UseSearchWidgetSkeletonReturnInterface {
	showSkeleton: boolean;
	isFadingOut: boolean;
}

/**
 * Orquesta la visibilidad del skeleton de formulario de un widget de búsqueda.
 *
 * - RN3: mantiene el skeleton al menos 150ms para evitar el "flash of skeleton".
 * - RN4: limita el skeleton a un máximo de 3s; superado el tiempo se libera el
 *   render para que el widget continúe con su flujo natural (fallback).
 * - Aplica un fade-out de 200ms antes de desmontar para que el reemplazo no
 *   genere saltos de layout.
 */
const useSearchWidgetSkeleton = (isLoading: boolean): UseSearchWidgetSkeletonReturnInterface => {
	const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
	const [isFadingOut, setIsFadingOut] = useState<boolean>(false);
	const mountedAtRef = useRef<number>(Date.now());
	useEffect(() => {
		const maxTimeoutId = setTimeout(() => {
			setShowSkeleton(false);
		}, MAXIMUM_VISIBLE_MS);

		return () => {
			clearTimeout(maxTimeoutId);
		};
	}, []);

	useEffect(() => {
		if (isLoading || isFadingOut) {
			return;
		}

		const elapsed: number = Date.now() - mountedAtRef.current;
		const remainingMinimum: number = Math.max(MINIMUM_VISIBLE_MS - elapsed, 0);
		const fadeStartId = setTimeout(() => {
			setIsFadingOut(true);
		}, remainingMinimum);

		return () => {
			clearTimeout(fadeStartId);
		};
	}, [isLoading, isFadingOut]);

	useEffect(() => {
		if (!isFadingOut) {
			return;
		}

		const hideTimeoutId = setTimeout(() => {
			setShowSkeleton(false);
		}, FADE_OUT_MS);

		return () => {
			clearTimeout(hideTimeoutId);
		};
	}, [isFadingOut]);

	return {
		showSkeleton,
		isFadingOut,
	};
};

export default useSearchWidgetSkeleton;
